/**
 * What-If Rebalancing Simulator
 * Allows users to explore different portfolio allocations and see projected outcomes
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, RotateCcw, TrendingUp, TrendingDown, Shield } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { cn } from '@/lib/utils';

interface Holding {
  ticker: string;
  shares: number;
  currentPrice: number;
  weight: number;
}

interface WhatIfSimulatorProps {
  holdings: Holding[];
  totalValue: number;
  currentVolatility: number;
  targetVolatility: number;
  className?: string;
}

interface AllocationChange {
  ticker: string;
  currentWeight: number;
  newWeight: number;
}

export function WhatIfSimulator({
  holdings,
  totalValue,
  currentVolatility,
  targetVolatility,
  className,
}: WhatIfSimulatorProps) {
  // Initialize allocations with current weights
  const [allocations, setAllocations] = useState<AllocationChange[]>(() =>
    holdings.slice(0, 10).map(h => ({
      ticker: h.ticker,
      currentWeight: h.weight,
      newWeight: h.weight,
    }))
  );

  const [showProjection, setShowProjection] = useState(false);

  // Calculate metrics based on new allocations
  const metrics = useMemo(() => {
    // Simple volatility estimation based on concentration
    // More concentrated = higher volatility
    const top3Weight = allocations
      .slice()
      .sort((a, b) => b.newWeight - a.newWeight)
      .slice(0, 3)
      .reduce((sum, a) => sum + a.newWeight, 0);

    // Estimate new volatility (simplified model)
    const concentrationFactor = top3Weight / 0.30; // 30% is "neutral" top 3 weight
    const estimatedVolatility = currentVolatility * (0.7 + 0.3 * concentrationFactor);

    // Calculate trades needed
    const trades = allocations
      .filter(a => Math.abs(a.newWeight - a.currentWeight) > 0.01)
      .map(a => ({
        ticker: a.ticker,
        action: a.newWeight > a.currentWeight ? 'BUY' : 'SELL',
        amount: Math.abs(a.newWeight - a.currentWeight) * totalValue,
        shares: Math.abs(a.newWeight - a.currentWeight) * totalValue /
          (holdings.find(h => h.ticker === a.ticker)?.currentPrice || 100),
      }));

    // Generate projection data (10 years)
    const projectionData = [];
    const annualReturn = 0.07; // Assumed 7% annual return
    const currentValue = totalValue;

    for (let year = 0; year <= 10; year++) {
      const baseGrowth = Math.pow(1 + annualReturn, year);
      // Lower volatility = tighter band
      const volFactor = estimatedVolatility / 0.15; // Normalize to 15% as baseline
      projectionData.push({
        year,
        current: Math.round(currentValue * baseGrowth),
        optimistic: Math.round(currentValue * baseGrowth * (1 + 0.3 * volFactor)),
        pessimistic: Math.round(currentValue * baseGrowth * (1 - 0.25 * volFactor)),
      });
    }

    return {
      estimatedVolatility,
      volatilityChange: estimatedVolatility - currentVolatility,
      top3Weight,
      trades,
      projectionData,
      totalTradeValue: trades.reduce((sum, t) => sum + t.amount, 0),
    };
  }, [allocations, currentVolatility, totalValue, holdings]);

  const handleSliderChange = (ticker: string, value: number[]) => {
    setAllocations(prev =>
      prev.map(a => (a.ticker === ticker ? { ...a, newWeight: value[0] / 100 } : a))
    );
  };

  const handleReset = () => {
    setAllocations(
      holdings.slice(0, 10).map(h => ({
        ticker: h.ticker,
        currentWeight: h.weight,
        newWeight: h.weight,
      }))
    );
    setShowProjection(false);
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatPct = (value: number) => `${(value * 100).toFixed(1)}%`;

  if (!holdings || holdings.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center">
          <p className="text-sm text-muted-foreground">
            Add holdings to use the What-If Simulator
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("col-span-full", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            What-If Rebalancing Simulator
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RotateCcw size={14} className="mr-1" />
            Reset
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Adjust allocation sliders to see how changes would affect your portfolio risk and projected growth.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Allocation Sliders */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Adjust Allocations</h4>
          <div className="grid gap-4">
            {allocations.map(alloc => (
              <div key={alloc.ticker} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{alloc.ticker}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {formatPct(alloc.currentWeight)}
                    </span>
                    <ArrowRight size={12} className="text-muted-foreground" />
                    <span className={cn(
                      "text-xs font-mono font-medium",
                      alloc.newWeight > alloc.currentWeight && "text-status-good",
                      alloc.newWeight < alloc.currentWeight && "text-status-critical"
                    )}>
                      {formatPct(alloc.newWeight)}
                    </span>
                  </div>
                </div>
                <Slider
                  value={[alloc.newWeight * 100]}
                  onValueChange={(v) => handleSliderChange(alloc.ticker, v)}
                  max={50}
                  step={0.5}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Impact Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Est. Volatility</p>
            <p className="text-lg font-mono font-bold">
              {formatPct(metrics.estimatedVolatility)}
            </p>
            <div className={cn(
              "text-xs flex items-center gap-1",
              metrics.volatilityChange > 0 ? "text-status-warning" : "text-status-good"
            )}>
              {metrics.volatilityChange > 0 ? (
                <TrendingUp size={12} />
              ) : (
                <TrendingDown size={12} />
              )}
              {metrics.volatilityChange > 0 ? '+' : ''}{formatPct(metrics.volatilityChange)}
            </div>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Target Vol.</p>
            <p className="text-lg font-mono font-bold">{formatPct(targetVolatility)}</p>
            <Badge variant={
              Math.abs(metrics.estimatedVolatility - targetVolatility) < 0.02
                ? "default"
                : "secondary"
            } className="text-[10px]">
              {Math.abs(metrics.estimatedVolatility - targetVolatility) < 0.02
                ? "On target"
                : "Off target"}
            </Badge>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Top 3 Weight</p>
            <p className="text-lg font-mono font-bold">{formatPct(metrics.top3Weight)}</p>
            <p className="text-xs text-muted-foreground">
              {metrics.top3Weight > 0.4 ? "Concentrated" : "Diversified"}
            </p>
          </div>

          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Trades Needed</p>
            <p className="text-lg font-mono font-bold">{metrics.trades.length}</p>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(metrics.totalTradeValue)} total
            </p>
          </div>
        </div>

        {/* Trades List */}
        {metrics.trades.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Required Trades</h4>
            <div className="space-y-1.5">
              {metrics.trades.map(trade => (
                <div
                  key={trade.ticker}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/20"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant={trade.action === 'BUY' ? 'default' : 'destructive'} className="text-[10px]">
                      {trade.action}
                    </Badge>
                    <span className="text-sm font-medium">{trade.ticker}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono">{formatCurrency(trade.amount)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      ~{trade.shares.toFixed(1)} shares
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projection Chart */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">10-Year Projection</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowProjection(!showProjection)}
            >
              {showProjection ? 'Hide' : 'Show'} Projection
            </Button>
          </div>

          {showProjection && (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={metrics.projectionData}>
                  <defs>
                    <linearGradient id="projectionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="year"
                    tickFormatter={(v) => `${v}y`}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tickFormatter={(v) => formatCurrency(v)}
                    tick={{ fontSize: 10 }}
                    width={60}
                  />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), '']}
                    labelFormatter={(label) => `Year ${label}`}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      fontSize: '12px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="pessimistic"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    fill="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="current"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#projectionGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="optimistic"
                    stroke="hsl(var(--status-good))"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    fill="none"
                  />
                  <ReferenceLine
                    y={totalValue}
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="3 3"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] text-muted-foreground text-center">
          This simulator uses simplified models for illustration purposes only.
          Past performance does not guarantee future results. Consult a financial advisor before making investment decisions.
        </p>
      </CardContent>
    </Card>
  );
}
